// Admin API configuration
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || 'http://localhost/DiceAce12/backend';

// Helper function to handle API requests
let apiRequest = async (endpoint, method = 'GET', data = null, token = null) => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers = {
        'Content-Type': 'application/json',
    };
    
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    const config = {
        method,
        headers,
        credentials: 'include', // Include cookies if needed
    };
    
    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE')) {
        config.body = JSON.stringify(data);
    }
    
    try {
        const response = await fetch(url, config);
        
        // Handle HTTP errors
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw {
                status: response.status,
                message: errorData.error || `HTTP error! status: ${response.status}`,
                data: errorData
            };
        }
        
        // Parse JSON response
        const responseData = await response.json();
        return responseData;
        
    } catch (error) {
        // console.error('Admin API Request Error:', error);
        
        // Handle network errors
        if (error.message && error.message.includes('NetworkError') || 
            error.message && error.message.includes('Failed to fetch')) {
            throw {
                status: 0,
                message: 'Network error. Please check your connection.',
                data: {}
            };
        }
        
        // Re-throw the error for the calling code to handle
        throw error;
    }
};

// Token management for admin
export const AdminTokenService = {
    getToken: () => {
        return localStorage.getItem('admin_token');
    },
    
    setToken: (token) => {
        localStorage.setItem('admin_token', token);
    },
    
    removeToken: () => {
        localStorage.removeItem('admin_token');
    },
    
    getAdminData: () => {
        const adminStr = localStorage.getItem('admin_data');
        return adminStr ? JSON.parse(adminStr) : null;
    },
    
    setAdminData: (adminData) => {
        localStorage.setItem('admin_data', JSON.stringify(adminData));
    },
    
    removeAdminData: () => {
        localStorage.removeItem('admin_data');
    },
    
    clear() {
        this.removeToken();
        this.removeAdminData();
        localStorage.removeItem('admin_permissions');
    },    
    
    getPermissions: () => {
        const permissionsStr = localStorage.getItem('admin_permissions');
        return permissionsStr ? JSON.parse(permissionsStr) : [];
    },
    
    setPermissions: (permissions) => {
        localStorage.setItem('admin_permissions', JSON.stringify(permissions));
    },
    
    hasPermission: (permission) => {
        const permissions = this.getPermissions();
        const adminData = this.getAdminData();
        
        // Super admin has all permissions
        if (adminData && adminData.role === 'super_admin') {
            return true;
        }
        
        return permissions.includes(permission);
    }
};

// Admin Authentication API
export const AdminAuthAPI = {
    // Admin login
    login: async (username, password) => {
        const response = await apiRequest('/api/admin/auth/login', 'POST', {
            username,
            password
        });
        
        if (response.token) {
            AdminTokenService.setToken(response.token);
            if (response.admin) {
                AdminTokenService.setAdminData(response.admin);
                if (response.admin.permissions) {
                    AdminTokenService.setPermissions(response.admin.permissions);
                }
            }
        }
        
        return response;
    },
    
    // Get admin profile
    getProfile: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/auth/profile', 'GET', null, token);
    },
    
    // Change admin password
    changePassword: async (currentPassword, newPassword) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/auth/change-password', 'POST', {
            current_password: currentPassword,
            new_password: newPassword
        }, token);
    },
    
    // Admin Management (Super Admin only)
    
    // Create new admin
    createAdmin: async (adminData) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/auth/create', 'POST', adminData, token);
    },
    
    // Get all admins (paginated)
    getAdmins: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryString = new URLSearchParams(params).toString();
        const url = `/api/admin/auth/admins${queryString ? `?${queryString}` : ''}`;
        return await apiRequest(url, 'GET', null, token);
    },
    
    // Get admin by ID
    getAdmin: async (adminId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/auth/${adminId}/get`, 'GET', null, token);
    },
    
    // Update admin
    updateAdmin: async (adminId, adminData) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/auth/${adminId}/update`, 'PUT', adminData, token);
    },
    
    // Change admin password (by super admin)
    changeAdminPassword: async (adminId, newPassword) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/auth/${adminId}/change-password`, 'POST', {
            new_password: newPassword
        }, token);
    },
    
    // Delete admin (soft delete)
    deleteAdmin: async (adminId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/auth/${adminId}/delete`, 'DELETE', null, token);
    },
    
    // Get admin statistics
    getAdminStats: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/auth/stats', 'GET', null, token);
    },
    
    // Utility functions
    
    // Logout
    logout: () => {
        AdminTokenService.clear();
        window.location.href = '/admin/login';
    },
    
    // Get current admin
    getCurrentAdmin: () => {
        return AdminTokenService.getAdminData();
    },
    
    // Check if admin is authenticated
    isAuthenticated: () => {
        return !!AdminTokenService.getToken();
    },
    
    // Check permission
    hasPermission: (permission) => {
        return AdminTokenService.hasPermission(permission);
    },
    
    // Check if current admin is super admin
    isSuperAdmin: () => {
        const admin = AdminTokenService.getAdminData();
        return admin && admin.role === 'super_admin';
    },
    
    // Check if has specific role
    hasRole: (role) => {
        const admin = AdminTokenService.getAdminData();
        return admin && admin.role === role;
    },
    
    // Check if can perform action on module
    can: (module, action) => {
        const permissions = AdminTokenService.getPermissions();
        if (!permissions || !permissions[module]) return false;
        
        return permissions[module].includes(action);
    }
};

// Admin Users Management API
export const AdminUsersAPI = {
    // Get all users with pagination and filters
    getUsers: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/users${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Get user by ID
    getUser: async (userId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/users/${userId}`, 'GET', null, token);
    },
    
    // Get user statistics
    getUserStats: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/users/stats', 'GET', null, token);
    },
    
    // Update user status
    updateUserStatus: async (userId, status, reason = '') => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/users/update-status', 'PUT', {
            user_id: userId,
            status,
            reason
        }, token);
    },
    
    // Update user info
    updateUserInfo: async (userId, userData) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/users/update-info', 'PUT', {
            user_id: userId,
            ...userData
        }, token);
    },
    
    // Reset user password
    resetUserPassword: async (userId, newPassword) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/users/reset-password', 'POST', {
            user_id: userId,
            new_password: newPassword
        }, token);
    },
    
    // Search users
    searchUsers: async (searchTerm, page = 1, limit = 20) => {
        return await this.getUsers({
            search: searchTerm,
            page,
            limit
        });
    },
    
    // Export users (CSV)
    exportUsers: async (filters = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(filters).toString();
        const endpoint = `/api/admin/users/export${queryParams ? `?${queryParams}` : ''}`;
        
        const url = `${API_BASE_URL}${endpoint}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });
        
        if (!response.ok) {
            throw new Error('Failed to export users');
        }
        
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
    }
};

// Admin Wallet Management API
export const AdminWalletAPI = {
    // Get all transactions
    getTransactions: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/wallet/transactions${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Get transaction by ID
    getTransaction: async (transactionId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/wallet/${transactionId}`, 'GET', null, token);
    },
    
    // Update transaction status
    updateTransactionStatus: async (transactionId, status, notes = '') => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/wallet/${transactionId}/status`, 'PUT', {
            transaction_id: transactionId,
            status,
            notes
        }, token);
    },
    
    // Get wallet statistics
    getWalletStats: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/wallet/stats', 'GET', null, token);
    },
    
    // Manual deposit
    manualDeposit: async (userId, amount, description) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/wallet/manual-deposit', 'POST', {
            user_id: userId,
            amount: parseFloat(amount),
            description
        }, token);
    },
    
    // Manual withdrawal
    manualWithdrawal: async (userId, amount, description) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/wallet/manual-withdrawal', 'POST', {
            user_id: userId,
            amount: parseFloat(amount),
            description
        }, token);
    },
    
    // Check Bitcoin transaction
    checkBitcoinTransaction: async (txHash) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/wallet/check-bitcoin/${txHash}`, 'GET', null, token);
    },
    
    // Get daily statistics
    getDailyStats: async (startDate, endDate) => {
        return await this.getTransactions({
            start_date: startDate,
            end_date: endDate,
            order_by: 'created_at',
            order_dir: 'ASC'
        });
    },
    
    // Export transactions (CSV)
    exportTransactions: async (filters = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(filters).toString();
        const endpoint = `/api/admin/wallet/transactions/export${queryParams ? `?${queryParams}` : ''}`;
        
        const url = `${API_BASE_URL}${endpoint}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });
        
        if (!response.ok) {
            throw new Error('Failed to export transactions');
        }
        
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `transactions_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
    },

       // Get all wallets with pagination
    getWallets: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/wallets${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Get wallet by ID
    getWallet: async (walletId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/wallets/${walletId}`, 'GET', null, token);
    },
    
    // Get wallet by user ID
    getWalletByUserId: async (userId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/wallets/user/${userId}`, 'GET', null, token);
    },
    
    // Adjust wallet balance
    adjustBalance: async (walletId, amount, description) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/wallets/adjust-balance', 'POST', {
            wallet_id: walletId,
            amount: parseFloat(amount),
            description
        }, token);
    },
    
    // Lock/unlock wallet
    toggleWalletLock: async (walletId, locked, reason = '') => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/wallets/toggle-lock', 'POST', {
            wallet_id: walletId,
            locked: locked ? 1 : 0,
            reason
        }, token);
    },
    
    // Get wallet audit logs
    getWalletLogs: async (walletId, params = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/wallets/${walletId}/logs${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Export wallets (CSV)
    exportWallets: async (filters = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(filters).toString();
        const endpoint = `/api/admin/wallets/export${queryParams ? `?${queryParams}` : ''}`;
        
        const url = `${API_BASE_URL}${endpoint}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });
        
        if (!response.ok) {
            throw new Error('Failed to export wallets');
        }
        
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `wallets_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
    },

       // Get all wallets with pagination
    getWallets: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/wallets${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Get wallet by ID
    getWallet: async (walletId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/wallets/${walletId}`, 'GET', null, token);
    },
    
    // Get wallet by user ID
    getWalletByUserId: async (userId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/wallets/user/${userId}`, 'GET', null, token);
    },
    
    // Adjust wallet balance
    adjustBalance: async (walletId, amount, description) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/wallets/adjust-balance', 'POST', {
            wallet_id: walletId,
            amount: parseFloat(amount),
            description
        }, token);
    },
    
    // Lock/unlock wallet
    toggleWalletLock: async (walletId, locked, reason = '') => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/wallets/toggle-lock', 'POST', {
            wallet_id: walletId,
            locked: locked ? 1 : 0,
            reason
        }, token);
    },
    
    // Get wallet audit logs
    getWalletLogs: async (walletId, params = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/wallets/${walletId}/logs${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Export wallets (CSV)
    exportWallets: async (filters = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(filters).toString();
        const endpoint = `/api/admin/wallets/export${queryParams ? `?${queryParams}` : ''}`;
        
        const url = `${API_BASE_URL}${endpoint}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });
        
        if (!response.ok) {
            throw new Error('Failed to export wallets');
        }
        
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `wallets_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
    },

    // Get deposit proofs with filters
    getDepositProofs: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/deposits${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },

    // Get deposit proof stats
    getDepositProofStats: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/deposits/stats', 'GET', null, token);
    },

    // Process deposit proof (approve/reject)
    processDepositProof: async (proofId, action, notes = '') => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/deposits/process', 'POST', {
            proof_id: proofId,
            action: action,
            notes: notes
        }, token);
    },

    // Get pending deposits count
    getPendingDepositsCount: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/deposits/pending/count', 'GET', null, token);
    },

    getSystemDepositAddress: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/deposit-address', 'GET', null, token);
    },

    // Update system deposit address
    updateSystemDepositAddress: async (address) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/deposit-address', 'POST', {
            address: address
        }, token);
    }

};

// Admin KYC Management API
export const AdminKYCAPI = {
    // Get KYC submissions
    getKYCSubmissions: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/kyc/submissions${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Get KYC submission by ID
    getKYCSubmission: async (kycId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/kyc/${kycId}`, 'GET', null, token);
    },
    
    // Verify KYC submission
    verifyKYC: async (kycId, status, rejectionReason = '') => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/kyc/verify', 'POST', {
            kyc_id: kycId,
            status,
            rejection_reason: rejectionReason
        }, token);
    },
    
    // Get KYC statistics
    getKYCStats: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/kyc/stats', 'GET', null, token);
    },
    
    // Download KYC document
    downloadDocument: async (kycId, documentType) => {
        const token = AdminTokenService.getToken();
        const endpoint = `/api/admin/kyc/download/${kycId}/${documentType}`;
        
        const url = `${API_BASE_URL}${endpoint}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });
        
        if (!response.ok) {
            throw new Error('Failed to download document');
        }
        
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        
        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `kyc_document_${kycId}_${documentType}`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }
        
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
    },
    
    // Get pending KYC count
    getPendingKYCCount: async () => {
        const response = await this.getKYCSubmissions({ status: 0, limit: 1 });
        return response.pagination?.total || 0;
    }
};

// Admin System API
export const AdminSystemAPI = {
    getSystemStats: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/system/stats', 'GET', null, token);
    },
    
    // Get admin activity logs
    getActivityLogs: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/system/activity-logs${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Clear cache
    clearCache: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/system/clear-cache', 'POST', null, token);
    },
    
    // Backup database
    backupDatabase: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/system/backup', 'POST', null, token);
    },
    
    // Get server status
    getServerStatus: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/system/status', 'GET', null, token);
    },

};

// Admin Settings API
// export const AdminSettingsAPI = {
//     // Get system settings
//     getSettings: async () => {
//         const token = AdminTokenService.getToken();
//         return await apiRequest('/api/admin/settings', 'GET', null, token);
//     },
    
//     // Update system settings
//     updateSettings: async (settings) => {
//         const token = AdminTokenService.getToken();
//         return await apiRequest('/api/admin/settings', 'PUT', settings, token);
//     },
    
//     // Get Bitcoin settings
//     getBitcoinSettings: async () => {
//         const token = AdminTokenService.getToken();
//         return await apiRequest('/api/admin/settings/bitcoin', 'GET', null, token);
//     },
    
//     // Update Bitcoin settings
//     updateBitcoinSettings: async (settings) => {
//         const token = AdminTokenService.getToken();
//         return await apiRequest('/api/admin/settings/bitcoin', 'PUT', settings, token);
//     },
    
//     // Get email settings
//     getEmailSettings: async () => {
//         const token = AdminTokenService.getToken();
//         return await apiRequest('/api/admin/settings/email', 'GET', null, token);
//     },
    
//     // Update email settings
//     updateEmailSettings: async (settings) => {
//         const token = AdminTokenService.getToken();
//         return await apiRequest('/api/admin/settings/email', 'PUT', settings, token);
//     },
    
//     // Test email configuration
//     testEmail: async (testEmail) => {
//         const token = AdminTokenService.getToken();
//         return await apiRequest('/api/admin/settings/test-email', 'POST', {
//             email: testEmail
//         }, token);
//     }
// };

// Admin Admins Management API
export const AdminAdminsAPI = {
    // Get all admins
    getAdmins: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/admins', 'GET', null, token);
    },
    
    // Create admin
    createAdmin: async (adminData) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/admins', 'POST', adminData, token);
    },
    
    // Update admin
    updateAdmin: async (adminId, adminData) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/admins/${adminId}`, 'PUT', adminData, token);
    },
    
    // Delete admin
    deleteAdmin: async (adminId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/admins/${adminId}`, 'DELETE', null, token);
    },
    
    // Get admin logs
    getAdminLogs: async (adminId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/admins/${adminId}/logs`, 'GET', null, token);
    }
};

// Utility functions for admin
export const AdminAPIUtils = {
    // Format numbers
    formatNumber: (num, decimals = 2) => {
        if (num === null || num === undefined) return '0';
        return parseFloat(num).toFixed(decimals);
    },
    
    // Format Bitcoin
    formatBTC: (amount) => {
        if (amount === null || amount === undefined) return '0.00000000';
        return parseFloat(amount).toFixed(8);
    },
    
    
    // Format date for display
    formatDate: (dateString, format = 'datetime') => {
        const date = new Date(dateString);
        
        if (format === 'date') {
            return date.toLocaleDateString();
        } else if (format === 'time') {
            return date.toLocaleTimeString();
        } else {
            return date.toLocaleString();
        }
    },
    
    // Format time ago
    timeAgo: (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);
        
        let interval = seconds / 31536000;
        if (interval > 1) return Math.floor(interval) + ' years ago';
        
        interval = seconds / 2592000;
        if (interval > 1) return Math.floor(interval) + ' months ago';
        
        interval = seconds / 86400;
        if (interval > 1) return Math.floor(interval) + ' days ago';
        
        interval = seconds / 3600;
        if (interval > 1) return Math.floor(interval) + ' hours ago';
        
        interval = seconds / 60;
        if (interval > 1) return Math.floor(interval) + ' minutes ago';
        
        return Math.floor(seconds) + ' seconds ago';
    },
    
    // Get status badge color
    getStatusColor: (status) => {
        switch (status) {
            case 'active':
            case 'completed':
            case 'verified':
                return 'success';
            case 'pending':
                return 'warning';
            case 'suspended':
            case 'failed':
            case 'rejected':
                return 'danger';
            case 'banned':
                return 'dark';
            default:
                return 'secondary';
        }
    },
    
    // Get transaction type color
    getTransactionTypeColor: (type) => {
        switch (type) {
            case 'deposit':
                return 'success';
            case 'withdrawal':
                return 'warning';
            case 'bet':
                return 'info';
            case 'win':
                return 'success';
            case 'bonus':
                return 'primary';
            default:
                return 'secondary';
        }
    },
    
    // Download file
    downloadFile: (data, filename, type = 'text/csv') => {
        const blob = new Blob([data], { type });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    },
    
    // Handle API errors with notifications
    handleError: (error, showNotification = true) => {
        // console.error('Admin API Error:', error);
        
        const errorMessage = error.message || 
                           error.data?.message || 
                           'An error occurred. Please try again.';
        
        if (showNotification) {
            // You can integrate with your notification system
            // For example, using toast notifications
            if (typeof window !== 'undefined') {
                // Using alert for simplicity, replace with your preferred notification system
                alert(`Error: ${errorMessage}`);
            }
        }
        
        // Handle authentication errors
        if (error.status === 401) {
            AdminTokenService.clear();
            window.location.href = '/admin/login';
        }
        
        return error;
    }
};

export const AdminSettingsAPI = {
    // Get all settings
    getSettings: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryString = new URLSearchParams(params).toString();
        const url = `/api/admin/settings${queryString ? `?${queryString}` : ''}`;
        return await apiRequest(url, 'GET', null, token);
    },
    
    // Get setting by key
    getSetting: async (key) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/settings/${key}`, 'GET', null, token);
    },
    
    // Get public settings (no auth required)
    getPublicSettings: async () => {
        return await apiRequest('/api/admin/settings/public', 'GET');
    },
    
    // Update single setting
    updateSetting: async (key, value) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/settings/update', 'PUT', {
            key,
            value
        }, token);
    },
    
    // Update multiple settings
    updateMultipleSettings: async (settings) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/settings/update-multiple', 'PUT', {
            settings
        }, token);
    },
    
    // Create new setting
    createSetting: async (settingData) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/settings/create', 'POST', settingData, token);
    },
    
    // Reset setting to default
    resetSetting: async (key) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/settings/reset/${key}`, 'POST', null, token);
    },
    
    // Get settings categories
    getCategories: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/settings/categories', 'GET', null, token);
    },
    
    // Get setting history
    getSettingHistory: async (key, params = {}) => {
        const token = AdminTokenService.getToken();
        const queryString = new URLSearchParams(params).toString();
        const url = `/api/admin/settings/history/${key}${queryString ? `?${queryString}` : ''}`;
        return await apiRequest(url, 'GET', null, token);
    },
    
    // Check if user has permission to manage settings
    canManageSettings: () => {
        const admin = AdminTokenService.getAdminData();
        return admin && (admin.role === 'super_admin' || admin.role === 'admin');
    },
    
    // Check if user can modify settings (only super admin)
    canModifySettings: () => {
        const admin = AdminTokenService.getAdminData();
        return admin && admin.role === 'super_admin';
    },

    // ===== BTC PRICE MANAGEMENT =====
    
    // Get BTC price with metadata
    getBTCPrice: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/settings/btc-price', 'GET', null, token);
    },
    
    // Update BTC price (manual or auto)
    updateBTCPrice: async (data) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/settings/btc-price', 'POST', data, token);
    },
    
    // Get BTC price history
    getBTCPriceHistory: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryString = new URLSearchParams(params).toString();
        const url = `/api/admin/settings/btc-price-history${queryString ? `?${queryString}` : ''}`;
        return await apiRequest(url, 'GET', null, token);
    },
    
    // Set manual price
    setManualBTCPrice: async (price, reason) => {
        return await AdminSettingsAPI.updateBTCPrice({
            manual_price: price,
            reason: reason
        });
    },
    
    // Force auto-update
    forceBTCUpdate: async () => {
        return await AdminSettingsAPI.updateBTCPrice({
            auto_update: true
        });
    },
    
    // Enable auto-update
    enableBTCAutoUpdate: async () => {
        return await AdminSettingsAPI.updateBTCPrice({
            enable_auto_update: true
        });
    },
    
    // Disable auto-update
    disableBTCAutoUpdate: async () => {
        return await AdminSettingsAPI.updateBTCPrice({
            enable_auto_update: false
        });
    }
};

// Admin Notifications API
export const AdminNotificationsAPI = {
    // Get all notifications
    getNotifications: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/notifications${queryString ? `?${queryString}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Get unread count
    getUnreadCount: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/notifications/unread-count', 'GET', null, token);
    },
    
    // Mark notification as read
    markAsRead: async (notificationId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/notifications/mark-read', 'POST', {
            notification_id: notificationId
        }, token);
    },
    
    // Mark all as read
    markAllAsRead: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/notifications/mark-all-read', 'POST', null, token);
    },
    
    // Delete notification
    deleteNotification: async (notificationId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/notifications/delete', 'POST', {
            notification_id: notificationId
        }, token);
    },
    
    // Get notifications by type
    getByType: async (type, limit = 10) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/notifications/by-type?type=${type}&limit=${limit}`, 'GET', null, token);
    },
    
    // Get recent notifications (last 24 hours)
    getRecentNotifications: async (limit = 10) => {
        const params = {
            limit: limit,
            sort: 'created_at:desc'
        };
        return await AdminNotificationsAPI.getNotifications(params);
    }
};

export const AdminBetsAPI = {
    // Get all bets with filters
    getBets: async (params = {}) => {
        const token = AdminTokenService.getToken();
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/api/admin/bets${queryString ? `?${queryString}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Get bet statistics
    getBetStats: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/bets/stats', 'GET', null, token);
    },
    
    // Get single bet details
    getBet: async (betId) => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/bets/${betId}`, 'GET', null, token);
    },
    
    // Update bet status
    updateBetStatus: async (betId, status, notes = '') => {
        const token = AdminTokenService.getToken();
        return await apiRequest(`/api/admin/bets/${betId}/status`, 'PUT', {
            status,
            notes
        }, token);
    }
};

export const AdminHomeImageAPI = {
    // Get all home page images with full details (Admin only)
    getAllImages: async () => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/home-settings', 'GET', null, token);
    },

    // Upload a new image (Admin only)
    uploadImage: async (key, file) => {
        const token = AdminTokenService.getToken();
        const formData = new FormData();
        formData.append('image', file);
        formData.append('key', key);
        
        const url = `${API_BASE_URL}/api/admin/home-settings/upload`;
        
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw {
                    status: response.status,
                    message: errorData.error || `HTTP error! status: ${response.status}`,
                    data: errorData
                };
            }
            
            return await response.json();
        } catch (error) {
            console.error('Upload error:', error);
            throw error;
        }
    },

    // Update a setting value (Admin only)
    updateSetting: async (key, value) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/home-settings/update', 'PUT', { key, value }, token);
    },

    // Update only the description (Admin only)
    updateDescription: async (key, description) => {
        const token = AdminTokenService.getToken();
        return await apiRequest('/api/admin/home-settings/update-description', 'PUT', { key, description }, token);
    }
};


// Request interceptor for admin
const originalAdminApiRequest = apiRequest;
apiRequest = async (endpoint, method = 'GET', data = null, token = null) => {
    // If token is not provided, try to get it from storage
    if (!token) {
        token = AdminTokenService.getToken();
    }
    
    return await originalAdminApiRequest(endpoint, method, data, token);
};

// Export the modified apiRequest
export { apiRequest };

// Export all admin APIs
export default {
    AdminAuthAPI,
    AdminUsersAPI,
    AdminWalletAPI,
    AdminKYCAPI,
    AdminSystemAPI,
    AdminSettingsAPI,
    AdminAdminsAPI,
    AdminAPIUtils,
    AdminTokenService,
    AdminNotificationsAPI,
    AdminBetsAPI,
    AdminHomeImageAPI,
    apiRequest
};